// @flow

import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, useField, useForm } from "react-final-form";
import classnames from "classnames";
import { OnChange } from "react-final-form-listeners";

import type { FieldRenderProps } from "react-final-form";
import { fetchLocations as fetchLocationsAlt } from "redux/actions/locations";
import { fetchStreets } from "redux/actions/street";

import ChangeServiceFormSharedContext from "modules/ChangeServiceForm/misc/context";

import {
  WhenFieldChanges,
  Row,
  Label,
  Column,
  ColumnContainer,
} from "modules/ChangeServiceForm/components/shared/CommonComponents";
import { read } from "modules/ChangeServiceForm/misc/utilities";

import { isPostalCode } from "modules/ChangeServiceForm/misc/validations";
import { mapLocations } from "Commons/Components/FormComponents/misc/Mappers";
import { getStreets, getLocations as getLocationAlt } from "redux/sagas/selectors";

import * as translations from "flavours/app-remind.me/i18n/validation.json";
import StreetNumber from "./StreetNumber";
import StreetName from "./StreetName";
import PostalCode from "./PostalCode";
import Cities from "./City";

const LocationsHandler = ({ fieldNames }: { fieldNames: Dict }) => {
  const {
    initialValues,
    configurationData: {
      translations: { fieldLabels },
    },
    category: { categoryType },
    inheritedStyles: {
      formRow,
      formRowPaired,
      formLabelLight,
      formColumn,
      formColumnContainer,
      columnContainerMobileDivided,
      columnContainerDesktopDivided,
      columnContainerMobileFull,
      columnMobileW1,
      columnDesktopW1,
      columnDesktopW2,
      columnMobileW3,
      columnDesktopW3,
      columnMobileFull,
    },
    steps: { stepName },
  } = useContext(ChangeServiceFormSharedContext);

  const form = useForm();
  const dispatch = useDispatch();

  const locationIdField = useField(fieldNames.locationId);
  const locationField = useField(fieldNames.location);
  const postalCodeField = useField(fieldNames.postalcode);
  const locationMainNameField = useField(fieldNames.locationMainName);
  const locationPartNameField = useField(fieldNames.locationPartName);
  const streetField = useField(fieldNames.street);
  const postalCodePreSelection = initialValues[categoryType]?.postalcode;
  const locationMainNamePreSelection = initialValues[categoryType]?.locationMainName;

  /* Locations */
  const locationsAlt = useSelector((state) => {
    const currentLocations = getLocationAlt(state, categoryType.toLowerCase(), postalCodeField.input.value);
    return currentLocations;
  });

  const createLocations = async (postalCodeFieldValue) => {
    try {
      await dispatch(fetchLocationsAlt(postalCodeFieldValue, categoryType.toLowerCase(), "changeService", "location"));
      preSelectUniqueLocation(locationsAlt?.map(mapLocations));
      invalidPostalCodeWarning("off");
    } catch (error) {
      console.error(error);
      invalidPostalCodeWarning("on");
    }
  };

  const preSelectUniqueLocation = (dropdownLocations) => {
    if (dropdownLocations?.length === 1) {
      locationIdField.input.onChange(dropdownLocations[0].value);
    }
  };

  const invalidPostalCodeWarning = (status) => {
    if (status === "off") form.mutators.setFieldData(fieldNames.postalcode, { error: undefined });
    if (status === "on") form.mutators.setFieldData(fieldNames.postalcode, { error: translations.invalidPostalCode });
  };

  /* Streets */
  const streets = useSelector((state) => {
    const currentStreets = getStreets(state, postalCodeField.input.value, locationMainNameField.input.value);
    const defaultOptions = [
      {
        Name: "",
      },
    ];
    return currentStreets ? currentStreets : defaultOptions;
  });

  const streetOptions = streets.map((street) => {
    return {
      value: street.Name,
      text: street.Name,
    };
  });

  /* Streets fetching trigger on City pre-selection */
  useEffect(() => {
    if (fieldNames?.postalcode === "postalcode") {
      if (
        !streetField.input.value &&
        locationMainNamePreSelection &&
        locationMainNamePreSelection === locationMainNameField.input.value &&
        postalCodePreSelection === postalCodeField.input.value
      ) {
        dispatch(fetchStreets(postalCodePreSelection, locationMainNamePreSelection, "changeService", "postalcode"));
      }
    }
  }, [initialValues[categoryType]?.postalcode]);

  /* Locations fetching trigger on postalcode pre-selection */
  useEffect(() => {
    if (fieldNames?.postalcode === "postalcode") {
      if (postalCodePreSelection && postalCodePreSelection === postalCodeField.input.value) {
        dispatch(fetchLocationsAlt(postalCodePreSelection, categoryType.toLowerCase(), "changeService", "location"));
      }
    }
  }, []);

  return (
    <Field name={fieldNames.locations}>
      {({ input: { onChange, value }, meta }: FieldRenderProps) => {
        return (
          <>
            <Row className={formRow}>
              <ColumnContainer
                className={formColumnContainer}
                mobile={columnContainerMobileFull}
                desktop={columnContainerDesktopDivided}
              >
                <Column className={formColumn} mobile={columnMobileFull} desktop={columnDesktopW2}>
                  <PostalCode fieldName={fieldNames.postalcode} stepName={stepName} />
                </Column>
                <Column className={formColumn} mobile={columnMobileFull} desktop={columnDesktopW2}>
                  <Cities
                    locations={locationsAlt?.map(mapLocations) || null}
                    fieldName={fieldNames.locationId}
                    stepName={stepName}
                  />
                </Column>
              </ColumnContainer>
            </Row>

            <Row className={classnames(formRow, formRowPaired)}>
              <Label className={formLabelLight}>{read(fieldLabels.contact.street)}</Label>
              <ColumnContainer
                className={formColumnContainer}
                mobile={columnContainerMobileDivided}
                desktop={columnContainerDesktopDivided}
              >
                <Column className={formColumn} mobile={columnMobileW1} desktop={columnDesktopW1}>
                  <StreetName locations={streetOptions || null} fieldName={fieldNames.street} stepName={stepName} />
                </Column>
                <Column className={formColumn} mobile={columnMobileW3} desktop={columnDesktopW3}>
                  <StreetNumber fieldName={fieldNames.streetNumber} stepName={stepName} />
                </Column>
              </ColumnContainer>
            </Row>
            <OnChange name={fieldNames.postalcode}>
              {(postalCodeFieldValue) => {
                if (!postalCodeFieldValue || isPostalCode(postalCodeFieldValue) !== undefined) {
                  return;
                }
                if (isPostalCode(postalCodeFieldValue) === undefined) {
                  locationIdField.input.onChange(undefined);
                  createLocations(postalCodeFieldValue);
                  return;
                }
              }}
            </OnChange>
            <OnChange name={fieldNames.locationId}>
              {(locationId) => {
                let selectedCity =
                  locationsAlt?.map(mapLocations) &&
                  locationsAlt?.map(mapLocations).find((location) => location.value === locationId);
                if (selectedCity) {
                  locationField.input.onChange(selectedCity.text);
                  locationMainNameField.input.onChange(selectedCity.mainname);
                  locationPartNameField.input.onChange(selectedCity.partname);
                  dispatch(
                    fetchStreets(postalCodeField.input.value, selectedCity.mainname, "changeService", "postalcode")
                  );
                }
              }}
            </OnChange>
            <WhenFieldChanges field={fieldNames.locations} becomes={null} set={fieldNames.locationId} to={null} />
          </>
        );
      }}
    </Field>
  );
};

export default LocationsHandler;
